import type {SuggestItem} from '@features/search/interfaces/SuggestItem';

export type BrandSuggestionSortProperty = 'brand' | 'name';

export type SortDirection = 'asc' | 'desc';

export interface BrandSuggestionSortOption {
    property: BrandSuggestionSortProperty;
    direction: SortDirection;
    isActive: boolean;
}

export type BrandSuggestionColumn =
    | {property: BrandSuggestionSortProperty; title: string; sortable: true}
    | {property: 'article'; title: string; sortable: false};

export const BRAND_SUGGESTION_COLUMNS: BrandSuggestionColumn[] = [
    {property: 'brand', title: 'Бренд', sortable: true},
    {property: 'article', title: 'Артикул', sortable: false},
    {property: 'name', title: 'Наименование', sortable: true}
];

/** Пункты мобильной сортировки: латиница, затем кириллица */
export const BRAND_SUGGESTION_SORT_PRESETS: {
    label: string;
    property: BrandSuggestionSortProperty;
    direction: SortDirection;
}[] = [
    {label: 'Бренд A-Z (А-Я)', property: 'brand', direction: 'asc'},
    {label: 'Бренд Z-A (Я-А)', property: 'brand', direction: 'desc'},
    {label: 'Наименование A-Z (А-Я)', property: 'name', direction: 'asc'},
    {label: 'Наименование Z-A (Я-А)', property: 'name', direction: 'desc'}
];

const DEFAULT_DIRECTION: Record<BrandSuggestionSortProperty, SortDirection> = {
    brand: 'asc',
    name: 'asc'
};

export function getDefaultBrandSuggestionSortOptions(): BrandSuggestionSortOption[] {
    return [
        {property: 'brand', direction: 'asc', isActive: true},
        {property: 'name', direction: 'asc', isActive: false}
    ];
}

export function isDefaultBrandSuggestionSort(sortOptions: BrandSuggestionSortOption[]): boolean {
    const active = sortOptions.find((option) => option.isActive);

    return active?.property === 'brand' && active.direction === 'asc';
}

export function applyBrandSuggestionSort(
    sortOptions: BrandSuggestionSortOption[],
    property: BrandSuggestionSortProperty,
    direction: SortDirection
): BrandSuggestionSortOption[] {
    return sortOptions.map((option) =>
        option.property === property
            ? {...option, direction, isActive: true}
            : {...option, direction: 'asc', isActive: false}
    );
}

export function getNextBrandSuggestionSort(
    sortOptions: BrandSuggestionSortOption[],
    property: BrandSuggestionSortProperty
): BrandSuggestionSortOption[] {
    const current = sortOptions.find((option) => option.property === property);
    const direction = resolveDirection(current, property);

    return applyBrandSuggestionSort(sortOptions, property, direction);
}

function resolveDirection(
    current: BrandSuggestionSortOption | undefined,
    property: BrandSuggestionSortProperty
): SortDirection {
    if (!current || !current.isActive) {
        return DEFAULT_DIRECTION[property];
    }

    return current.direction === 'asc' ? 'desc' : 'asc';
}

function getSortValue(item: SuggestItem, property: BrandSuggestionSortProperty): string {
    if (property === 'brand') {
        return item.name || '';
    }

    return item.articleName || '';
}

export function sortBrandSuggestions(
    items: SuggestItem[],
    sortOptions: BrandSuggestionSortOption[]
): SuggestItem[] {
    const active = sortOptions.find((option) => option.isActive);

    if (!active) {
        return items;
    }

    return items
        .slice()
        .sort((a, b) =>
            compareBrandSuggestionText(
                getSortValue(a, active.property),
                getSortValue(b, active.property),
                active.direction
            )
        );
}

const LATIN_LETTER = /[A-Za-z]/;
const CYRILLIC_LETTER = /[\u0400-\u04FF]/;

function scriptGroup(value: string): number {
    for (const char of value) {
        if (LATIN_LETTER.test(char)) {
            return 0;
        }

        if (CYRILLIC_LETTER.test(char)) {
            return 1;
        }
    }

    return 0;
}

function compareBrandSuggestionText(a: string, b: string, direction: SortDirection): number {
    const groupA = scriptGroup(a);
    const groupB = scriptGroup(b);

    if (groupA !== groupB) {
        return groupA - groupB;
    }

    const result = a.localeCompare(b, groupA === 1 ? 'ru' : 'en', {
        numeric: true,
        sensitivity: 'base'
    });

    return direction === 'asc' ? result : -result;
}
